/** Эндпоинты беты: только с заголовком `X-Game-Mode: beta` (иначе 404 `not_found`). */
import { ChestType, WheelSector } from "../../balance/beta";
import { HeroId } from "../../common";
import type { EndpointDef } from "../endpoint";
import { PlayerState } from "../player";
import { z } from "zod";

/** Поставить героев в слоты базы: `slots[i]` — герой или `null`; длина не больше числа слотов, копий до `copiesMax`. */
export const BetaLoadoutRequest = z.object({ slots: z.array(HeroId.nullable()).max(16) });
export const BetaLoadoutResponse = z.object({ state: PlayerState });

/** Прокрутка колеса. `paid` — за кристаллы (бесплатная раз в сутки берётся сама, если доступна). */
export const BetaWheelSpinRequest = z.object({ paid: z.boolean().default(false) });
export const BetaWheelSpinResponse = z.object({
  /** Индекс выпавшего сектора (клиент крутит колесо до него). */
  sectorIndex: z.number().int().min(0).max(7),
  sector: WheelSector,
  /** Что получил игрок: монеты/кристаллы/осколки конкретного героя; для сундука и буста — пусто. */
  granted: z.object({
    coins: z.number().int().min(0),
    gems: z.number().int().min(0),
    heroId: HeroId.nullable(),
    shards: z.number().int().min(0),
    chest: ChestType.nullable(),
    boostMinutes: z.number().int().min(0),
  }),
  free: z.boolean(),
  state: PlayerState,
});

/** Позиции магазина: монеты, премиум-герой, прокрутка, сундук. Заголовок `Idempotency-Key` обязателен по смыслу — повтор не списывает. */
export const BetaShopItem = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("coins"), packId: z.string() }),
  z.object({ kind: z.literal("hero"), heroId: HeroId }),
  z.object({ kind: z.literal("spin") }),
  z.object({ kind: z.literal("chest"), chest: ChestType }),
]);
export const BetaShopBuyRequest = z.object({ item: BetaShopItem });
export const BetaShopBuyResponse = z.object({
  /** Списано кристаллов; 0 — повтор запроса с тем же ключом. */
  spent: z.number().int().min(0),
  state: PlayerState,
});

/** Открыть сундук из инвентаря. */
export const BetaChestOpenRequest = z.object({ chestId: z.number().int().positive() });
export const BetaChestOpenResponse = z.object({
  chest: ChestType,
  rewards: z.array(
    z.object({
      kind: z.enum(["coins", "gems", "shards"]),
      amount: z.number().int().positive(),
      heroId: HeroId.nullable(),
    }),
  ),
  state: PlayerState,
});

/** Звезда героя за осколки. */
export const BetaStarUpRequest = z.object({ heroId: HeroId });
export const BetaStarUpResponse = z.object({ spentShards: z.number().int().min(0), state: PlayerState });

/** Счёт Telegram Stars на пакет кристаллов. Вне Telegram — 403. */
export const BetaGemsInvoiceRequest = z.object({ packId: z.string() });
export const BetaGemsInvoiceResponse = z.object({ url: z.url() });

export const betaEndpoints = {
  betaLoadout: {
    method: "POST",
    path: "/api/beta/loadout",
    request: BetaLoadoutRequest,
    response: BetaLoadoutResponse,
    auth: true,
  },
  betaWheelSpin: {
    method: "POST",
    path: "/api/beta/wheel/spin",
    request: BetaWheelSpinRequest,
    response: BetaWheelSpinResponse,
    auth: true,
  },
  betaShopBuy: {
    method: "POST",
    path: "/api/beta/shop/buy",
    request: BetaShopBuyRequest,
    response: BetaShopBuyResponse,
    auth: true,
  },
  betaChestOpen: {
    method: "POST",
    path: "/api/beta/chests/open",
    request: BetaChestOpenRequest,
    response: BetaChestOpenResponse,
    auth: true,
  },
  betaStarUp: {
    method: "POST",
    path: "/api/beta/stars/up",
    request: BetaStarUpRequest,
    response: BetaStarUpResponse,
    auth: true,
  },
  betaGemsInvoice: {
    method: "POST",
    path: "/api/beta/gems/invoice",
    request: BetaGemsInvoiceRequest,
    response: BetaGemsInvoiceResponse,
    auth: true,
  },
} as const satisfies Record<string, EndpointDef>;
